import { BreakpointTrackerContext } from '@jutro/layout';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { TranslatorContext } from '@jutro/locale';
import PropTypes from 'prop-types';
import _ from 'lodash';
import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import metadata from './PremiumFinanceDetails.metadata.json5';
import messages from './PremiumFinanceDetails.messages.js';
import styles from '../TWIAAdditionalInterestComponent.module.scss';

function PremiumFinanceDetails(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { item, saveData, cancelData, isInvalid, showErrors, onValidate } = props;
    const {
        isComponentValid,
        onValidate: setComponentValidation,
    } = useValidation('PremiumFinanceDetails');

    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useContext(TranslatorContext);
    const [additionalInfoVM, setAdditionalInfoVM] = useState(item);
    const isEmail = (email) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);
    const isUSZIPCode = (code) => /^[0-9]{5}(?:-[0-9]{4})?$/.test(code);
    const isCAZIPCode = (code) => /^[A-Z][0-9][A-Z] ?[0-9][A-Z][0-9]$/.test(code);
    const isJPDigitZIPCode = (code) => /^[0-9]{3}(?:-[0-9]{4})?$/.test(code);
    const isGBZIPCode = (code) => /^[A-Z0-9]{2,4} [A-Z0-9]{3}$/.test(code);
    const is5DigitZIPCode = (code) => /^[0-9]{5}$/.test(code);
    const isAUDigitZIPCode = (code) => /^[0-9]{4}$/.test(code);
    const [zipcodeValidationMessage, setZIPCodeValidationMessage] = useState([]);
    const [emailValidationMessage, setEmailValidationMessage] = useState([]);
    const [interestTypeValidationMessage, setInterestTypeValidationMessage] = useState([]);
    const [cityLabel, setCityLabel] = useState([]);
    const [zipCodeLabel, setZIPCodeLabel] = useState([]);
    const [stateLabel, setStateLabel] = useState([]);
    const { refreshData } = useDataRefresh();
    const countryCode = _.get(additionalInfoVM, 'primaryAddress.country.value.code');
    const mustStandardize = _.get(additionalInfoVM, 'primaryAddress.mustStandardize_Ext.value', false);
    const isNotValidated = _.get(additionalInfoVM, 'primaryAddress.isNotValidated_Ext.value');
    const isOverridden = _.get(additionalInfoVM, 'primaryAddress.isOverridden_Ext.value');
    const isValidated = _.get(additionalInfoVM, 'primaryAddress.isValidated_Ext.value');
    const [placeholderMsg, setPlaceHolderMsg] = useState('');
    const postalCode = _.get(additionalInfoVM, 'primaryAddress.postalCode.value');
    const [cedexBureauValidationMessage, setCedexBureauValidationMessage] = useState([]);

    useEffect(() => {
        if (onValidate && !_.isNull(isComponentValid)) {
            onValidate(isComponentValid, 'PremiumFinanceDetails');
        }
    }, [isComponentValid, onValidate]);

    useEffect(() => {
        isInvalid(isComponentValid, 'PremiumFinanceDetails');
    }, [isInvalid, isComponentValid])

    useEffect(() => {
        getLabel(countryCode);
        _.set(additionalInfoVM, 'primaryAddress.mustStandardize_Ext.value', false);
    }, [countryCode]);

    const getLabel = (value) => {
        setZIPCodeLabel([]);
        setCityLabel([]);
        setStateLabel([]);

        switch (value) {
            case 'US':
                setZIPCodeLabel('ZIP Code');
                setCityLabel('City');
                setStateLabel('State');
                setPlaceHolderMsg(messages.pleaseChoose);
                break;
            case 'CA':
                setZIPCodeLabel('Postal Code');
                setCityLabel('City');
                setStateLabel('Province');
                setPlaceHolderMsg(messages.pleaseChoose);
                break;
            case 'GB':
                setCityLabel('Town/City');
                setZIPCodeLabel('Postcode');
                setStateLabel('State');
                setPlaceHolderMsg(messages.pleaseChoose);
                break;
            case 'JP':
                setCityLabel('City (phonetic)');
                setZIPCodeLabel('Postcode');
                setStateLabel('Prefecture');
                setPlaceHolderMsg(messages.pleaseChoose);
                break;
            default:
                setCityLabel('City');
                setZIPCodeLabel('Postcode');
                setStateLabel('State');
                setPlaceHolderMsg(messages.pleaseChoose);
                break;
        }
    }

    const getAdditionalInterestTypeValues = useCallback(() => {
        const availableInterestTypeValues = additionalInfoVM.policyContactRoles.children[0].policyAdditionalInterest.cpAdditionalInterestDetails_TWIA.children[0].type.aspects.availableValues

        if (availableInterestTypeValues.length > 0) {
            const availableInterestType = availableInterestTypeValues.map((code) => {
                return {
                    code: code.code,
                    name: translator({ id: code.name })
                }
            })

            return availableInterestType
        }
    }, [additionalInfoVM, translator]);

    const getAvailablePremiumFinaceCompany = useMemo(() => {
        const availablePremiumFinanceValues = viewModelService.productMetadata.get('pc').types.getTypelist('typekey.PremiumFinCompany_TWIA').codes.map((code) => {
            return {
                code: code.code,
                name: translator({ id: code.name })
            }
        })

        return availablePremiumFinanceValues
    }, [viewModelService, translator]);


    const countryChange = useCallback((value, path) => {
        _.set(additionalInfoVM, 'primaryAddress.state', undefined)
        
        if (postalCode !== undefined) {
            _.set(additionalInfoVM, 'primaryAddress.postalCode', undefined)
            setZIPCodeValidationMessage([translator(messages.Required)]) // DE15937  
        }

        getLabel(value);
        _.set(additionalInfoVM, path, value);
        setAdditionalInfoVM(additionalInfoVM);
        refreshData();
    }, [additionalInfoVM, postalCode, refreshData, translator]);


    const validateZIPCode = useCallback(
        (value, path) => {
            _.set(additionalInfoVM, path, value);
            setZIPCodeValidationMessage([]);

            if (!value) {
                setZIPCodeValidationMessage([translator(messages.Required)])
            } else if (
                (countryCode === 'US' && !isUSZIPCode(value)) ||
                (countryCode === 'CA' && !isCAZIPCode(value)) ||
                (countryCode === 'FR' && !is5DigitZIPCode(value)) ||
                (countryCode === 'DE' && !is5DigitZIPCode(value)) ||
                (countryCode === 'GB' && !isGBZIPCode(value)) ||
                (countryCode === 'AU' && !isAUDigitZIPCode(value)) ||
                (countryCode === 'JP' && !isJPDigitZIPCode(value))
            ) {
                setZIPCodeValidationMessage([translator(messages.ZIPCodeValidationMessage)])
            }

            setAdditionalInfoVM(additionalInfoVM);
        },
        [additionalInfoVM, countryCode, translator]);

    const validateEmail = useCallback(
        (value, path) => {
            _.set(additionalInfoVM, path, value);
            setEmailValidationMessage([]);

            if (!value) {

                setEmailValidationMessage([]);
            } else if (!isEmail(value)) {

                setEmailValidationMessage([translator(messages.EmailValidationMessage)])
            }

            setAdditionalInfoVM(additionalInfoVM);
        },
        [additionalInfoVM, translator]);

    // DE15937    
    const validateInterestType = useCallback((value, path)=>{
        _.set(additionalInfoVM, path, value);
           
        if(!value){
            setInterestTypeValidationMessage([translator(messages.Required)])
        }else{
            setInterestTypeValidationMessage([])
        }

        setAdditionalInfoVM(additionalInfoVM);
    },[additionalInfoVM, translator])

    const cedexBureauChange = useCallback(
        (value, path) => {
            _.set(additionalInfoVM, path, value);
            setCedexBureauValidationMessage([]);

            if (value.length > 2) {

                setCedexBureauValidationMessage([translator(messages.cedexBureauMessage)])
            }

            setAdditionalInfoVM(additionalInfoVM);
        },
        [additionalInfoVM, translator]);

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        name: {
            visible: additionalInfoVM?.subtype.value === 'Company'
        },
        // DE15937
        interestType: {
            path: 'policyContactRoles.children[0].policyAdditionalInterest.cpAdditionalInterestDetails_TWIA.children[0].type',
            availableValues: additionalInfoVM ? getAdditionalInterestTypeValues() : [],
            validationMessages: showErrors && !isComponentValid && 
            _.get(additionalInfoVM,'policyContactRoles.children[0].policyAdditionalInterest.cpAdditionalInterestDetails_TWIA.children[0].type.value')===undefined?
            [translator(messages.Required)]:interestTypeValidationMessage
        },
        loanNumber: {
            path: 'policyContactRoles.children[0].policyAdditionalInterest.cpAdditionalInterestDetails_TWIA.children[0].contractNumber',
            required: true,
            showRequired: true
        },
        premiumFinaceCompany: {
            visible: additionalInfoVM?.subtype.value === 'Company' && _.get(additionalInfoVM, 'policyContactRoles.children[0].policyAdditionalInterest.cpAdditionalInterestDetails_TWIA.children[0].premiumFinanceCompanyType_TWIA.aspects.visible'),
            required: _.get(additionalInfoVM, 'policyContactRoles.children[0].policyAdditionalInterest.cpAdditionalInterestDetails_TWIA.children[0].premiumFinanceCompanyType_TWIA.aspects.required'),
            availableValues: getAvailablePremiumFinaceCompany,
            defaultValue: getAvailablePremiumFinaceCompany[0],
            path: 'policyContactRoles.children[0].policyAdditionalInterest.cpAdditionalInterestDetails_TWIA.children[0].premiumFinanceCompanyType_TWIA',
        },
        cityKanji: {
            visible: countryCode === 'JP'
        },
        city: {
            label: cityLabel
        },
        state: {
            label: stateLabel,
            visible: countryCode === 'US' || countryCode === 'CA' || countryCode === 'AU' || countryCode === 'JP' || countryCode === 'DE',
            required: countryCode === 'US' || countryCode === 'CA' || countryCode === 'AU' || countryCode === 'JP' || countryCode === 'DE',
            showRequired: countryCode === 'US' || countryCode === 'CA' || countryCode === 'AU' || countryCode === 'JP' || countryCode === 'DE',
            placeholder: placeholderMsg
        },
        // DE15937
        zipCode: {
            label: zipCodeLabel,
            validationMessages: showErrors && !isComponentValid && 
            _.get(additionalInfoVM,'primaryAddress.postalCode.value')===undefined?
            [translator(messages.Required)]:zipcodeValidationMessage
        },
        cedex: {
            visible: countryCode === 'FR'
        },
        cedexBureau: {
            visible: countryCode === 'FR' && _.get(additionalInfoVM, 'primaryAddress.cedex_Ext.value') === true,
            validationMessages: cedexBureauValidationMessage
        },
        emailAddress: {
            validationMessages: emailValidationMessage
        },
        saveButton: {
            disabled: !isComponentValid
        }
    };


    const saveAdditionalInterest = () => saveData(additionalInfoVM);

    const cancelAdditionalInterest = () => {
        isInvalid(true, 'PremiumFinanceDetails');
        cancelData();
    }

    const writeValue = useCallback(
        (value, path) => {
            if (path === 'primaryAddress.country') {
                getLabel(value)
            }

            _.set(additionalInfoVM, `${path}.value`, value);
            setAdditionalInfoVM(additionalInfoVM);
            refreshData();
        },
        [additionalInfoVM, refreshData]
    );

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            OnValidateEmail: validateEmail,
            onSave: saveAdditionalInterest,
            onCancel: cancelAdditionalInterest,
            onCountryChange: countryChange,
            OnvalidateZIPCode: validateZIPCode,
            onValidateInterestType: validateInterestType,
            cedexBureauChange
        },
    };


    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={additionalInfoVM}
            overrideProps={overrideProps}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
            onValueChange={writeValue}
            onValidationChange={setComponentValidation}
            showErrors={showErrors}
        />
    );
}

PremiumFinanceDetails.propTypes = {
    item: PropTypes.shape({}).isRequired,
    saveData: PropTypes.func.isRequired
};

export default PremiumFinanceDetails;
